<template>
  <v-carousel
    ref="carousel"
    v-model="lightBoxIndex"
    height="auto"
    hide-delimiters
    :show-arrows="!hideArrows && images.length > 1"
  >
    <template #prev="{ props }">
      <v-btn
        variant="text"
        color="primary"
        :icon="mdiChevronLeft"
        aria-label="previous"
        @click="props.onClick"
      />
    </template>
    <template #next="{ props }">
      <v-btn
        variant="text"
        color="primary"
        :icon="mdiChevronRight"
        aria-label="next"
        @click="props.onClick"
      />
    </template>
    <v-carousel-item
      v-for="(image, index) in images"
      :key="index"
      content-class="cursor-pointer"
      eager
      aspect-ratio="1"
      :width="`${size}px`"
      :lazy-src="emptyPlaceholderImage"
      :src="fitImage(image, size, size)"
      :srcset="getSrcset(image, size, size)"
      :alt="altText"
      @click="showLightBox"
    >
      <v-fade-transition v-if="hoverEffect">
        <div>
          <v-overlay :model-value="isHovered" contained class="align-center justify-center">
            <v-icon :size="magnifySize" :icon="mdiMagnify" color="primary" />
          </v-overlay>
        </div>
      </v-fade-transition>
    </v-carousel-item>
    <v-carousel-item
      v-if="images.length === 0"
      :src="placeholderImage"
      :width="`${size}px`"
      aspect-ratio="1"
    />
    <teleport to="body">
      <vue-easy-lightbox
        :imgs="imagesWithAltText"
        :index="lightBoxIndex"
        :scroll-disabled="false"
        :visible="lightBoxShow"
        move-disabled
        @hide="hideLightBox"
        @on-index-change="updateIndex"
      />
    </teleport>
  </v-carousel>
</template>

<script setup lang="ts">
import { emptyPlaceholderImage, fitImage, getSrcset, placeholderImage } from '@/helpers'
import { mdiChevronLeft, mdiChevronRight, mdiMagnify } from '@mdi/js'
import { useElementHover } from '@vueuse/core'
import { computed, PropType, ref } from 'vue'

const componentProps = defineProps({
  images: {
    type: Array as PropType<string[]>,
    required: true
  },
  altText: {
    type: String,
    required: true
  },
  size: {
    type: Number,
    default: 600
  },
  hideArrows: {
    type: Boolean,
    default: false
  },
  hoverEffect: {
    type: Boolean,
    default: false
  }
})

const lightBoxShow = ref(false)
const lightBoxIndex = ref(0)

const showLightBox = () => {
  lightBoxShow.value = true
}

const hideLightBox = () => {
  lightBoxShow.value = false
  isHovered.value = false
}

const updateIndex = (_: number, newIndex: number) => {
  lightBoxIndex.value = newIndex
}

const carousel = ref()
const isHovered = useElementHover(carousel)

const magnifySize = computed(() => Math.max(32, componentProps.size / 8))

const imagesWithAltText = computed(() => {
  return componentProps.images.map((image) => ({
    src: image,
    alt: componentProps.altText
  }))
})
</script>
